import React, { useState, useEffect, FormEvent } from 'react';
import DeviceSelectionScreen from './DeviceSelectionScreen/DeviceSelectionScreen';
import IntroContainer from '../IntroContainer/IntroContainer';
import MediaErrorSnackbar from './MediaErrorSnackbar/MediaErrorSnackbar';
import RoomNameScreen from './RoomNameScreen/RoomNameScreen';
import { useAppState } from '../../state';
import { useParams } from 'react-router-dom';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { AppError, reportError } from '../../utils/errors';

export enum Steps {
  roomNameStep,
  deviceSelectionStep,
}

export default function PreJoinScreens() {
  const { user } = useAppState();
  const { getAudioAndVideoTracks } = useVideoContext();
  const { URLRoomName } = useParams<{ URLRoomName?: string }>();

  const [step, setStep] = useState(Steps.deviceSelectionStep);

  const [name, setName] = useState<string>(user?.displayName || '');
  const [roomName, setRoomName] = useState<string>('');
  const [roomPassword, setRoomPassword] = useState<string>('');

  const [mediaError, setMediaError] = useState<Error | AppError>();

  useEffect(() => {
    if (URLRoomName) {
      setRoomName(URLRoomName);
      if (user?.displayName) {
        setStep(Steps.deviceSelectionStep);
      }
    }
  }, [user, URLRoomName]);

  useEffect(() => {
    if (step === Steps.deviceSelectionStep && !mediaError) {
      getAudioAndVideoTracks().catch(error => {
        setMediaError(
          reportError(error, {
            source: 'media',
            technicalMessage: 'Failed to acquire local media tracks',
            userMessage: error.message,
            retryable: true,
          })
        );
      });
    }
  }, [getAudioAndVideoTracks, step, mediaError]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // If this app is deployed as a twilio function, don't change the URL because routing isn't supported.
    // @ts-ignore
    if (!window.location.origin.includes('twil.io') && !window.STORYBOOK_ENV) {
      window.history.replaceState(null, '', window.encodeURI(`/room/${roomName}${window.location.search || ''}`));
    }
    setStep(Steps.deviceSelectionStep);
  };

  // Detect permission denied errors for mic or camera
  const isPermissionDenied =
    mediaError?.name === 'NotAllowedError' || mediaError?.message?.toLowerCase().includes('denied');

  return (
    <IntroContainer>
      <MediaErrorSnackbar error={mediaError} />

      {isPermissionDenied && (
        <div style={{ maxWidth: 400, margin: '20px auto', color: '#dc3545', textAlign: 'center' }}>
          <p>
            It looks like you denied camera and/or microphone access. Please allow access from your browser's site
            settings (click the lock icon near the URL) and reload the page to grant permissions when prompted.
          </p>
        </div>
      )}

      {/* Only show RoomNameScreen or DeviceSelectionScreen if no permission denied */}
      {!isPermissionDenied && (
        <>
          {step === Steps.roomNameStep && (
            <RoomNameScreen
              name={name}
              roomName={roomName}
              roomPassword={roomPassword}
              setName={setName}
              setRoomName={setRoomName}
              setRoomPassword={setRoomPassword}
              handleSubmit={handleSubmit}
            />
          )}

          {step === Steps.deviceSelectionStep && (
            <DeviceSelectionScreen name={name} roomName={roomName} roomPassword={roomPassword} setStep={setStep} />
          )}
        </>
      )}
    </IntroContainer>
  );
}
